<template>
  <div
    class="flex-1 gap-4 p-4 border border-solid border-gray-300 bg-white rounded-lg"
  >
    <div class="flex flex-col pb-3">
      <span
        class="text-gray-800 font-sans text-lg font-semibold leading-6 tracking-tight"
        >Lead Field Mapping</span
      >
    </div>
    <div class="flex flex-col gap-4">
      <!-- First Name -->
      <div class="flex flex-wrap gap-2 justify-between items-center">
        <BaseLabel
          forAttr="firstName"
          text="First Name"
          class="text-gray-600 font-sans text-sm font-medium leading-5"
          required
        />
        <BaseSearchableDropdown
          v-model="localFirstName"
          :options="leadMappingFields.map((field) => field.label)"
          placeholder="Select"
          class="w-32 md:w-64"
        />
      </div>

      <!-- Last Name -->
      <div class="flex flex-wrap gap-2 justify-between items-center">
        <BaseLabel
          forAttr="lastName"
          text="Last Name"
          class="text-gray-600 font-sans text-sm font-medium leading-5"
          required
        />
        <BaseSearchableDropdown
          v-model="localLastName"
          :options="leadMappingFields.map((field) => field.label)"
          placeholder="Select"
          class="w-32 md:w-64"
        />
      </div>

      <!-- Company -->
      <div class="flex flex-wrap gap-2 justify-between items-center">
        <BaseLabel
          forAttr="company"
          text="Company"
          class="text-gray-600 font-sans text-sm font-medium leading-5"
          required
        />
        <BaseSearchableDropdown
          v-model="localCompany"
          :options="leadMappingFields.map((field) => field.label)"
          placeholder="Select"
          class="w-32 md:w-64"
        />
      </div>

      <!-- Title -->
      <div class="flex flex-wrap gap-2 justify-between items-center">
        <BaseLabel
          forAttr="title"
          text="Title"
          class="text-gray-600 font-sans text-sm font-medium leading-5"
          required
        />
        <BaseSearchableDropdown
          v-model="localTitle"
          :options="leadMappingFields.map((field) => field.label)"
          placeholder="Select"
          class="w-32 md:w-64"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, watch } from "vue";
import BaseLabel from "@/components/shared/BaseLabel.vue";
import BaseSearchableDropdown from "@/components/shared/BaseSearchableDropdown.vue";

export default defineComponent({
  name: "LeadFieldMapping",
  components: {
    BaseLabel,
    BaseSearchableDropdown,
  },
  props: {
    leadFirstName: {
      type: String,
      required: true,
    },
    leadLastName: {
      type: String,
      required: true,
    },
    leadCompany: {
      type: String,
      required: true,
    },
    leadTitle: {
      type: String,
      required: true,
    },
    leadMappingFields: {
      type: Array as PropType<{ label: string; name: string }[]>,
      required: true,
    },
  },
  setup(props, { emit }) {
    const localFirstName = ref(props.leadFirstName);
    const localLastName = ref(props.leadLastName);
    const localCompany = ref(props.leadCompany);
    const localTitle = ref(props.leadTitle);

    watch(
      [() => props.leadMappingFields, () => props.leadFirstName],
      ([fields, firstName]) => {
        if (fields?.length) {
          const matchingField = fields.find(
            (field) => field.name === firstName
          );
          localFirstName.value = matchingField?.label || "";
        }
      },
      { immediate: true }
    );

    watch(
      [() => props.leadMappingFields, () => props.leadLastName],
      ([fields, lastName]) => {
        if (fields?.length) {
          const matchingField = fields.find((field) => field.name === lastName);
          localLastName.value = matchingField?.label || "";
        }
      },
      { immediate: true }
    );

    watch(
      [() => props.leadMappingFields, () => props.leadCompany],
      ([fields, company]) => {
        if (fields?.length) {
          const matchingField = fields.find((field) => field.name === company);
          localCompany.value = matchingField?.label || "";
        }
      },
      { immediate: true }
    );

    watch(
      [() => props.leadMappingFields, () => props.leadTitle],
      ([fields, title]) => {
        if (fields?.length) {
          const matchingField = fields.find((field) => field.name === title);
          localTitle.value = matchingField?.label || "";
        }
      },
      { immediate: true }
    );

    watch(localFirstName, () => {
      const value = props.leadMappingFields.find(
        (field) => field.label === localFirstName.value
      );
      emit("update:leadFirstName", value?.name || "");
    });

    watch(localLastName, () => {
      const value = props.leadMappingFields.find(
        (field) => field.label === localLastName.value
      );
      emit("update:leadLastName", value?.name || "");
    });

    watch(localCompany, () => {
      const value = props.leadMappingFields.find(
        (field) => field.label === localCompany.value
      );
      emit("update:leadCompany", value?.name || "");
    });

    watch(localTitle, () => {
      const value = props.leadMappingFields.find(
        (field) => field.label === localTitle.value
      );
      emit("update:leadTitle", value?.name || "");
    });

    return {
      localFirstName,
      localLastName,
      localCompany,
      localTitle,
    };
  },
});
</script>
